import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useEdges } from 'reactflow';
import { NodeBehaviorHook } from '../../registry/types';
import { NodeEmptyState } from '../../components/nodes/NodeEmptyState';
import { hasIncomingEdge, resolveNodeEmptyReason } from '../../utils/nodeEmptyState';
import { useGrammarInputState } from '../../hook/useGrammarInputState';
import { NodeType, VisInteractionType } from '../../constants';
import { useProvenanceContext } from '../../providers/ProvenanceProvider';
import { useFlowContext } from '../../providers/FlowProvider';
import { useToastContext } from '../../providers/ToastProvider';
import { fetchData } from '../../services/api';
import { formatDate, mapTypes } from '../../utils/formatters';
import { ICodeDataContent } from '../../types';
import { resolveImageColumns } from '../../utils/imageColumns';
import ContentTable from './components/ContentTable';
import { CopyButton } from '../../components/CopyButton';
import ImageCardGrid from './components/ImageCardGrid';
import { toRows } from '../../utils/rowSource';
import { unwrapValueEnvelopes } from '../../utils/sandboxEnvelope';
import { registerNodeSettingWriter } from '../../utils/nodeSettingsWriters';
import nodeSettings from '../../contracts/nodeSettings.json';

/** The picker's label, and its word for every image column, which an agent's
 *  review card and the backend say the setting in too
 *  (contracts/nodeSettings.json). */
const IMAGE_COLUMN_LABEL = nodeSettings.simpleVis.settings.imageColumn;
const ALL_IMAGE_COLUMNS_WORD = nodeSettings.simpleVis.all;

function buildTableRows(parsedOutput: ICodeDataContent): any[] {
  // Was a fourth copy of the same column-major flatten; see utils/rowSource.
  return toRows(parsedOutput as any);
}

type SimpleVisMode = 'table' | 'image' | 'text';

/** Sentinel for "show every image column", the default. */
export const ALL_IMAGE_COLUMNS = 'all';

function toDisplayString(input: any): string {
  // The payload's own envelope, whatever its type, then the ones the sandbox
  // puts around each element of a list (#516).
  const value = unwrapValueEnvelopes(input?.data !== undefined ? input.data : input);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function isFrame(input: any): boolean {
  const dt = input?.dataType;
  return dt === 'dataframe' || dt === 'geodataframe';
}

/** The node's persisted image-column choice; see the picker below. */
export function resolveImageColumnChoice(data: any): string {
  const raw = data?.simpleVis?.imageColumn;
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  return trimmed || ALL_IMAGE_COLUMNS;
}

/**
 * What to render, derived once from a resolved payload.
 *
 * Rows come first and the mode follows from them. That ordering is the fix for
 * #276: the previous check read `input.data.image_id`, a DataFrame *column
 * map*, so a GeoDataFrame, whose columns live under `features[i].properties`,
 * could never be images however it was shaped, and a column of image URLs had
 * no path at all. `buildTableRows` already flattened both shapes; now one
 * answer serves the table, the images and the interaction flags alike.
 */
function deriveView(parsedInput: any): {
  mode: SimpleVisMode;
  rows: any[];
  imageColumns: string[];
  textContent: string;
} {
  // Nothing wired in yet, or an upstream that has not run. There is no payload
  // to describe, so every branch stays empty and the caller falls through to
  // the empty state that says which of those it is (#224). Without this guard a
  // freshly dropped node has `input === ''`, whose JSON is the two-character
  // string `""`, which is truthy and renders as a text payload.
  if (parsedInput == null || parsedInput === '') {
    return { mode: 'text', rows: [], imageColumns: [], textContent: '' };
  }
  const rows = isFrame(parsedInput) ? buildTableRows(parsedInput) : [];
  const imageColumns = resolveImageColumns(rows);
  if (imageColumns.length > 0) return { mode: 'image', rows, imageColumns, textContent: '' };
  if (isFrame(parsedInput)) return { mode: 'table', rows, imageColumns: [], textContent: '' };
  return { mode: 'text', rows: [], imageColumns: [], textContent: toDisplayString(parsedInput) };
}

export const useSimpleVisBehavior: NodeBehaviorHook = (data, nodeState) => {
  // Which of the empty states this is depends on whether anything is wired in,
  // which only the graph knows.
  const edges = useEdges();
  const connected = hasIncomingEdge(edges, data.nodeId);
  // Whether the node feeding it ran and failed, asked as the Data Pool asks it
  // (#347); on a dashboard, a viewer's run making the rows, or why it stopped
  // before them.
  const { upstreamErrored, upstreamRunning, upstreamStopped, onDashboard } = useGrammarInputState(data.nodeId);
  // Lazy init: if input is already present on mount (e.g. in tests) seed the
  // view so the first render already produces a contentComponent. The effect
  // will overwrite this once it fetches any path reference.
  const initial = useMemo(() => deriveView(data.input), []);
  const [currentMode, setCurrentMode] = useState<SimpleVisMode>(initial.mode);
  const [rows, setRows] = useState<any[]>(initial.rows);
  const [imageColumns, setImageColumns] = useState<string[]>(initial.imageColumns);
  const [textContent, setTextContent] = useState<string>(initial.textContent);
  const [interactions, _setInteractions] = useState<any>({});
  const interactionsRef = useRef(interactions);
  const dataInputBypass = useRef(false);
  // While the rows of the input that just arrived are read (a fetch of its
  // path): the body says what it shows is coming, not that the input is not a
  // table. Told in the render that brings the input, not an effect after it:
  // an input is read once the read of it, the latest, has ended. The one the
  // node mounted with is its first view above.
  const [readInput, setReadInput] = useState<unknown>(data.input);
  const latestInputRef = useRef<unknown>(data.input);
  const reading = data.input != null && data.input !== '' && data.input !== readInput;

  const setInteractions = (newData: any) => {
    interactionsRef.current = newData;
    _setInteractions(newData);
  };

  const { nodeExecProv } = useProvenanceContext();
  const { workflowNameRef, updateDataNode } = useFlowContext();
  const { showToast } = useToastContext();

  useEffect(() => {
    const handleInput = async () => {
      const shouldProcess = dataInputBypass.current;
      dataInputBypass.current = true;
      if (!shouldProcess) return;
      if (data.input == null || data.input === '') return;

      const input = data.input;
      latestInputRef.current = input;
      try {
        const startTime = formatDate(new Date());
        const typesInput = input.dataType ? [input.dataType] : [];

        let parsedInput = input;
        if (parsedInput.path) {
          try {
            parsedInput = await fetchData(parsedInput.path);
          } catch (err) {
            showToast('Error fetching data. Please try again.', 'error');
            return;
          }
        }

        nodeExecProv(startTime, startTime, workflowNameRef.current, data.nodeId, mapTypes(typesInput), mapTypes(typesInput), '');

        const view = deriveView(parsedInput);
        setCurrentMode(view.mode);
        setRows(view.rows);
        setImageColumns(view.imageColumns);
        setTextContent(view.textContent);

        // Downstream first, then the outcome: the outcome is what tells a Run All
        // this node is done, and the nodes it feeds must have the rows by then.
        if (typeof data.outputCallback === 'function') {
          data.outputCallback(data.nodeId, input);
        }
        nodeState.setOutput({ code: 'success', content: parsedInput });
      } finally {
        // A failed read ends it too: the body then says what it has.
        if (latestInputRef.current === input) setReadInput(input);
      }
    };

    handleInput();
  }, [data.input]);

  useEffect(() => {
    if (typeof data.interactionsCallback === 'function') {
      data.interactionsCallback(interactions, data.nodeId);
    }
  }, [interactions]);

  // A card is a row, so the index sent downstream is the row index. It used to
  // be the position in a flattened image list, which only matched when every
  // cell held exactly one image.
  const clickRow = useCallback((rowIndex: number) => {
    setInteractions({
      images_click: {
        type: VisInteractionType.POINT,
        data: [rowIndex],
        priority: 1,
        source: NodeType.VIS_SIMPLE,
      },
    });
  }, []);

  // Written back by a linked Data Pool, onto the row itself for both frame
  // shapes, so this no longer has to know which shape it came from.
  const interacted = useMemo(
    () => rows.map((row) => (row?.interacted === '1' ? '1' : '0')),
    [rows],
  );

  const choice = resolveImageColumnChoice(data);
  const shownImageColumns = useMemo(() => {
    if (choice === ALL_IMAGE_COLUMNS) return imageColumns;
    const pinned = imageColumns.filter((c) => c === choice);
    // A pinned column the current frame does not carry falls back to all,
    // rather than rendering an empty node and blaming the data.
    return pinned.length > 0 ? pinned : imageColumns;
  }, [choice, imageColumns]);

  // All clears the setting: the node shows every image column, as one that
  // never picked a column does. A dataflow saved before keeps the word "all",
  // which the node still reads as every column.
  const pickColumn = useCallback(
    (value: string) => {
      const simpleVis = { ...(data as any).simpleVis };
      if (value === ALL_IMAGE_COLUMNS) delete simpleVis.imageColumn;
      else simpleVis.imageColumn = value;
      updateDataNode(data.nodeId, { ...data, simpleVis });
    },
    [data, updateDataNode],
  );

  // An agent's applied change picks the column as the picker does
  // (utils/nodeSettingsWriters); one that names no column picks All.
  const pickColumnRef = useRef(pickColumn);
  pickColumnRef.current = pickColumn;
  useEffect(
    () => registerNodeSettingWriter(data.nodeId, 'simpleVis', (setting) =>
      pickColumnRef.current(setting.imageColumn || ALL_IMAGE_COLUMNS)),
    [data.nodeId],
  );

  // Memoize so the JSX reference is stable across re-renders. NodeEditor
  // auto-switches to the "output" tab whenever `contentComponent` changes
  // identity — without this, any re-render (e.g. React Flow deselecting the
  // node on a pane click) would yank the user out of the code editor.
  const contentComponent = useMemo<React.ReactNode | undefined>(() => {
    if (currentMode === 'table' && rows.length > 0) {
      return <ContentTable tableData={rows} nodeId={data.nodeId} />;
    }
    if (currentMode === 'image' && rows.length > 0 && shownImageColumns.length > 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {imageColumns.length > 1 && (
            <label
              className="nodrag"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', fontSize: 11 }}
            >
              <span>{IMAGE_COLUMN_LABEL}</span>
              <select
                aria-label={IMAGE_COLUMN_LABEL}
                data-curio-image-column="true"
                value={choice}
                onChange={(e) => pickColumn(e.target.value)}
                style={{ fontSize: 11 }}
              >
                <option value={ALL_IMAGE_COLUMNS}>{ALL_IMAGE_COLUMNS_WORD}</option>
                {imageColumns.map((column) => (
                  <option key={column} value={column}>{column}</option>
                ))}
              </select>
            </label>
          )}
          <div style={{ flex: 1, minHeight: 0 }}>
            <ImageCardGrid
              nodeId={data.nodeId}
              rows={rows}
              imageColumns={shownImageColumns}
              interacted={interacted}
              onClickRow={clickRow}
            />
          </div>
        </div>
      );
    }
    if (currentMode === 'text' && textContent) {
      // This pane is where an upstream error's text lands, so it is the string
      // a user most often needs to hand to an agent (#267). `.react-flow__node`
      // sets `user-select: none` so a drag pans the node, which made it
      // unselectable; `nodrag` releases the gesture and `userSelect` re-enables
      // the selection, the same pairing CodeEditor's output pane uses. `nowheel`
      // because the pane scrolls horizontally on its own.
      return (
        <div style={{ position: 'relative', height: '100%' }}>
          {/* Floated over the pane rather than above it: the node body is
              short, and a header row would cost a line of the text itself. */}
          <div
            className="nodrag"
            style={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}
          >
            <CopyButton value={textContent} label="Copy text" />
          </div>
          <pre
            className="nodrag nowheel"
            data-curio-node-text="true"
            style={{
              margin: 0,
              padding: '8px',
              fontSize: '12px',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              userSelect: 'text',
              cursor: 'text',
            }}
          >
            {textContent}
          </pre>
        </div>
      );
    }
    // Every branch above now requires actual content, so this is reached
    // whenever there is none — where it used to return `undefined` and
    // UniversalNode rendered nothing at all (#224). `ContentTable` with an
    // empty array was the same defect wearing a table: it drew an empty
    // <Table> and looked like a node that had failed.
    const reason = resolveNodeEmptyReason({
      connected,
      upstreamErrored,
      upstreamRunning,
      upstreamStopped,
      hasInput: data.input != null && data.input !== '',
      reading,
      onDashboard,
      // 'text' is the fallback mode for anything that is not a frame,
      // so a text mode with no content is a payload we cannot show.
      tabular: currentMode !== 'text',
      rowCount: rows.length,
    }) ?? 'not-tabular';
    return (
      <NodeEmptyState
        reason={reason}
        hint={reason === 'upstream-stopped' ? upstreamStopped ?? undefined : undefined}
      />
    );
  }, [currentMode, rows, imageColumns, shownImageColumns, choice, interacted, textContent, data.nodeId, data.input, connected, upstreamErrored, upstreamRunning, upstreamStopped, reading, onDashboard, clickRow, pickColumn]);

  return {
    contentComponent,
    setSendCodeCallbackOverride: (_: any) => {},
  };
};
